import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { BugReports } from '@utils/api';
import { IBugsSubmit } from './bugs.types';
import ButtonText from './components/ButtonText';
import Alert from '@components/elements/Alert';
import {getMenus} from '@as_core/menus/SideMenu';
import { APP_CONFIG } from '@app_config/app';
import {
  FlexRow,
  FlexRowSpaced,
  FlexItem,
  FlexSpacer,
} from '@components/layout/FlexStyles';

import { TextTitle, TextSmall } from '@components/elements/TextStyles';
import useCognito from '@as_core/account/useCognito';
import InputField from '@as_core/components/inputs/InputField';
import { UserContext, UserT } from '@as_core/contexts/UserContext';
import SelectorDropdown from "@as_core/controls/inputs/SelectorDropdown";

type LocalErrorsT = {
  title: string;
  description: string;
  category: string;
};

const getCategories = (user: UserT) => {
  const menus = getMenus(user);
  return ['Authorization', ...menus.map((m) => m?.name), 'Other', 'Suggestion'];
};

const BugsSubmit = ({
  setIsNewBugReported,
  onSubmit,
  inEditBug,
  setInEditBug,
}: IBugsSubmit) => {
  const debug = false;
  const { user } = useContext(UserContext);
  const { getToken } = useCognito();
  const [bugCategories] = useState<string[]>(getCategories(user));
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [values, setValues] = useState({
    title: '',
    description: '',
    category: '',
  });
  const [errors, setErrors] = useState<LocalErrorsT>({
    title: null,
    description: null,
    category: null,
  });

  const handleAlert = () => {
    setIsNewBugReported(true);
    setAlertOpen(false);
    onSubmit();
  };

  const submitBugReport = async () => {
    if (debug) console.log('SUBMIT: title, description', values);

    const localErrors: LocalErrorsT = {
      title: null,
      description: null,
      category: null,
    };

    if (values.title === '') {
      localErrors.title = 'Title required*';
    }

    if (values.description === '') {
      localErrors.description = 'Description required*';
    }

    if (values.category === '') {
      localErrors.category = 'Category required*';
    }

    setErrors(localErrors);

    if (!Object.values(localErrors).find((error) => error !== null)) {
      const token = getToken();
      const payload = {
        system: APP_CONFIG.name,
        title: values.title,
        description: values.description,
        category: user.subApp + ':' + values.category,
      };

      if (inEditBug) {
        await BugReports.update(token, { ...payload, uuid: inEditBug.uuid });
        setInEditBug(null);
      } else {
        await BugReports.submit(token, payload);
        setIsNewBugReported(true);
      }
      setAlertMessage(
        `Issue/Request Successfully ${
          inEditBug ? 'Updated' : 'Submitted'
        }: ${new Date().toLocaleString()}`
      );
      setAlertOpen(true);
      setValues({
        title: '',
        description: '',
        category: '',
      });
    }
  };
  if (debug) console.log('title, description', values);

  useEffect(() => {
    if (inEditBug) {
      setValues({
        title: inEditBug?.title,
        description: inEditBug?.description,
        category: inEditBug?.category,
      });
      setErrors({
        title: null,
        description: null,
        category: null,
      });
    }
  }, [inEditBug]);

  const onCancelEdit = () => {
    setValues({
      title: '',
      description: '',
      category: '',
    });
    setInEditBug(null);
  };

  const onChange = (value: unknown, key: string) => {
    if (errors[key]) {
      setErrors((previous) => ({ ...previous, [key]: null }));
    }
    setValues((previous) => ({ ...previous, [key]: value }));
  };

  return (
    <>
      <Content>
        <FlexRowSpaced width={'100%'}>
          <FlexItem>
            <Heading color={'accent'}>
              {inEditBug
                ? 'Update a previously submitted issue/request'
                : 'Submit a newly discovered issue or suggestion'}
            </Heading>
          </FlexItem>
          <FlexItem>
            {inEditBug ? (
              <>
                <ButtonText width={100} label='Cancel' onClick={onCancelEdit} />
                <FlexSpacer width={10} />
              </>
            ) : null}
            <ButtonText
              width={100}
              label={inEditBug ? 'Update' : 'Submit'}
              onClick={submitBugReport}
            />
          </FlexItem>
        </FlexRowSpaced>
        <FlexRow>
          <FlexItem>
            <TextSmall>
              {inEditBug
                ? 'Edit either description, category or title'
                : 'Enter a short name and detailed description below.'}
            </TextSmall>
          </FlexItem>
        </FlexRow>
        <FlexRow width={'100%'}>
          <FlexItem width={'50%'}>
            <InputField
              id={'title'}
              type={'text'}
              placeholder={'Enter short title'}
              label={errors.title || 'Title'}
              value={values.title}
              onChange={(e) => onChange(e.target.value, 'title')}
              error={Boolean(errors.title)}
            />
          </FlexItem>
          <FlexItem width={'50%'} height={'30px'}>
            <SelectorDropdown
              value={values.category}
              label={'Category'}
              setValue={(v) => onChange(v, 'category')}
              width={200}
              options={bugCategories.map((o) => ({ value: o, label: o }))}
            />
          </FlexItem>
        </FlexRow>
        <FlexRow width={'100%'}>
          <FlexItem width={'100%'}>
            <InputField
              id='description'
              label={errors.description || 'Description'}
              value={values.description}
              multiline
              rows={3}
              onChange={(e) => onChange(e.target.value, 'description')}
              error={Boolean(errors.description)}
            />
          </FlexItem>
        </FlexRow>
      </Content>
      <Alert
        type={'general'}
        title={'Issue/Request Submitted'}
        message={alertMessage}
        alertOpen={alertOpen}
        closeAlert={handleAlert}
      />
    </>
  );
};

export default BugsSubmit;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 680px;
`;

const Heading = styled(TextTitle)<{
  children?: React.ReactNode;
}>`
  font-size: 18px;
`;
